
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  Building, 
  Calendar, 
  DollarSign, 
  Heart, 
  Share2,
  BarChart, 
  ArrowUpRight, 
  CheckCircle2,
  Bookmark,
  BookmarkPlus
} from 'lucide-react';
import { Progress } from "@/components/ui/progress";
import { JobDetailsProps } from '@/types/job';

export const JobDetails: React.FC<JobDetailsProps> = ({ 
  job, 
  open, 
  onClose, 
  onApply,
  matchScore,
  showMatchScore = false,
  isSaved = false,
  onToggleSave,
  onShare,
  hasStartedApplication = false
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center justify-between">
            <span>{job.title}</span>
            {showMatchScore && matchScore !== undefined && (
              <div className="flex items-center">
                <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1">
                  <BarChart className="h-3 w-3" />
                  <span>{matchScore}% Match</span>
                </Badge>
              </div>
            )}
          </DialogTitle>
          <div className="flex items-center text-gray-500 mt-1">
            <Building className="h-4 w-4 mr-1" />
            <span>{job.company}</span>
          </div>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-gray-700">
              <div className="bg-gray-100 p-2 rounded-full">
                <MapPin className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Location</div>
                <div className="font-medium">{job.location}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-700">
              <div className="bg-gray-100 p-2 rounded-full">
                <DollarSign className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Salary</div>
                <div className="font-medium">{job.salary}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-700">
              <div className="bg-gray-100 p-2 rounded-full">
                <Briefcase className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Job Type</div>
                <div className="font-medium">{job.type || job.jobType || "Full-time"}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-700">
              <div className="bg-gray-100 p-2 rounded-full">
                <Clock className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Hours</div>
                <div className="font-medium">{job.hours}</div>
              </div>
            </div>
          </div>
          
          <Separator />
          
          <Tabs defaultValue="description">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="description">Description</TabsTrigger>
              <TabsTrigger value="skills">Skills & Requirements</TabsTrigger>
            </TabsList>
            
            <TabsContent value="description" className="space-y-4 pt-4">
              <p className="text-gray-700 whitespace-pre-line">{job.description}</p>
              
              {job.remote && (
                <div className="bg-purple-50 border border-purple-100 p-3 rounded-lg mt-4">
                  <div className="flex items-center gap-2">
                    <div className="bg-purple-100 p-1 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-600 h-4 w-4">
                        <path d="M12 12h.01" />
                        <path d="M20 12h.01" />
                        <path d="M4 12h.01" />
                        <path d="M12 20h.01" />
                        <path d="M12 4h.01" />
                        <path d="M4 4h.01" />
                        <path d="M20 4h.01" />
                        <path d="M4 20h.01" />
                        <path d="M20 20h.01" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-medium text-purple-800">Remote Work Available</h3>
                  </div>
                  <p className="text-sm text-purple-700 mt-1 ml-7">This position offers remote work flexibility.</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="skills" className="pt-4">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Required Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {job.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-gray-100 text-gray-800">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {job.requirements && (
                  <div>
                    <h3 className="font-medium mb-2">Requirements</h3>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      {job.requirements.map((req, idx) => (
                        <li key={idx}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {matchScore !== undefined && showMatchScore && (
                  <div className="mt-6 border border-blue-100 rounded-lg p-4 bg-blue-50/50">
                    <h3 className="font-medium flex items-center gap-2 text-blue-800">
                      <BarChart className="h-4 w-4 text-blue-600" />
                      Skills Match
                    </h3>
                    
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Match Score</span>
                        <span className="font-medium text-blue-700">{matchScore}%</span>
                      </div>
                      <Progress value={matchScore} className="h-2 bg-blue-100" indicatorClassName="bg-blue-500" />
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-3">
                      This score is based on how well your profile matches the job requirements.
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          <Separator />
          
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex items-center text-gray-500 text-sm">
              <Calendar className="h-4 w-4 mr-1" />
              <span>Posted: {job.postedDate || job.posted}</span>
            </div>
            
            <div className="flex flex-wrap gap-2 justify-end">
              {onToggleSave && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2"
                  onClick={onToggleSave}
                >
                  {isSaved ? (
                    <Bookmark className="h-4 w-4 fill-blue-500 text-blue-500" />
                  ) : (
                    <BookmarkPlus className="h-4 w-4" />
                  )}
                  {isSaved ? 'Saved' : 'Save Job'}
                </Button>
              )}
              
              {onShare && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2"
                  onClick={onShare}
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </Button>
              )}
              
              <Button 
                size="sm" 
                className="gap-2 bg-blue-600 hover:bg-blue-700"
                onClick={onApply}
              >
                {hasStartedApplication ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <ArrowUpRight className="h-4 w-4" />
                )}
                {hasStartedApplication ? 'Continue Application' : 'Apply Now'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
